import { Injectable, TemplateRef } from "@angular/core";
import { BehaviorSubject } from "rxjs";

type GlobalSidebarTemplateType = "content" | "header" | "footer";

export type SidebarPosition = "left" | "right" | "bottom" | "top" | "full";

export type SidebarProps = Partial<{
    blockScroll: boolean;
    style: Record<string, any>;
    styleClass: string;
    autoZIndex: boolean;
    baseZIndex: number;
    modal: boolean;
    dismissible: boolean;
    position: SidebarPosition;
    fullScreen: boolean;
}>;

export type ResolvedSidebarProps = {
    blockScroll: boolean;
    style: Record<string, any>;
    styleClass: string;
    autoZIndex: boolean;
    baseZIndex: number;
    modal: boolean;
    dismissible: boolean;
    position: SidebarPosition;
    fullScreen: boolean;
};

@Injectable({
    providedIn: "root",
})
export class GlobalSidebarService {
    private lastId = -1;
    private _contentTemplate$ = new BehaviorSubject<
        TemplateRef<any> | undefined
    >(undefined);
    private _headerTemplate$ = new BehaviorSubject<
        TemplateRef<any> | undefined
    >(undefined);
    private _footerTemplate$ = new BehaviorSubject<
        TemplateRef<any> | undefined
    >(undefined);
    private _sidebarVisible$ = new BehaviorSubject(false);
    private _sidebarOptions$ = new BehaviorSubject<SidebarProps>({});

    sidebarOptions$ = this._sidebarOptions$.asObservable();
    sidebarVisible$ = this._sidebarVisible$.asObservable();
    contentTemplate$ = this._contentTemplate$.asObservable();
    headerTemplate$ = this._headerTemplate$.asObservable();
    footerTemplate$ = this._footerTemplate$.asObservable();

    getLastId() {
        return this.lastId;
    }

    sidebarVisible() {
        return this._sidebarVisible$.value;
    }

    setTemplate(
        tpl: TemplateRef<any> | undefined,
        type: GlobalSidebarTemplateType = "content",
    ) {
        switch (type) {
            case "content":
                this._contentTemplate$.next(tpl);
                break;
            case "header":
                this._headerTemplate$.next(tpl);
                break;
            case "footer":
                this._footerTemplate$.next(tpl);
                break;
        }
    }

    setOptions(opts: SidebarProps) {
        this._sidebarOptions$.next(opts);
    }

    open(id?: number) {
        if (id) this.lastId = id;
        this._sidebarVisible$.next(true);
    }

    close() {
        this._sidebarVisible$.next(false);
    }

    toggle() {
        this._sidebarVisible$.next(!this._sidebarVisible$.value);
    }

    reset() {
        this.lastId = -1;
        this._contentTemplate$.next(undefined);
        this._headerTemplate$.next(undefined);
        this._footerTemplate$.next(undefined);
        this._sidebarOptions$.next({});
        this._sidebarVisible$.next(false);
    }
}
